/**
 * Voice Notes index page — list, view, attach to patient, download, delete.
 *
 * Route: /voice-notes (mounted in App.js). Visible to clinical roles only;
 * unauthorized users redirected upstream.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Mic, Download, Trash2, User as UserIcon, FileText, RefreshCw, Loader2, Search, Plus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import VoiceNoteDialog from '../../components/voice/VoiceNoteDialog';
import { patientApi } from '../../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('savenes_token')}` });

export default function VoiceNotesPage() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [openNew, setOpenNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [patients, setPatients] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/voice-notes?mine_only=true`, { headers: auth() });
      setNotes(r.data.notes || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load voice notes');
    } finally { setLoading(false); }
  };

  const loadPatients = async () => {
    try {
      const r = await patientApi.list();
      setPatients(r.data.patients || r.data || []);
    } catch { setPatients([]); }
  };

  useEffect(() => { load(); loadPatients(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return notes;
    const needle = q.toLowerCase();
    return notes.filter(n => (
      (n.title || '').toLowerCase().includes(needle) ||
      (n.summary || '').toLowerCase().includes(needle) ||
      (n.patient_name || '').toLowerCase().includes(needle)
    ));
  }, [notes, q]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this voice note? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/voice-notes/${id}`, { headers: auth() });
      setNotes(notes.filter(n => n.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success('Note deleted');
    } catch { toast.error('Delete failed'); }
  };

  const handleDownload = async (note) => {
    try {
      const r = await axios.get(`${API_URL}/api/voice-notes/${note.id}/download`, { headers: auth(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(note.title || 'voice_note').replace(/\s+/g, '_')}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const handleAttachPatient = async (note, patientId) => {
    try {
      const r = await axios.patch(`${API_URL}/api/voice-notes/${note.id}`, { patient_id: patientId || null }, { headers: auth() });
      setNotes(notes.map(n => n.id === note.id ? r.data : n));
      if (selected?.id === note.id) setSelected(r.data);
      toast.success(patientId ? 'Attached to patient' : 'Detached');
    } catch { toast.error('Update failed'); }
  };

  return (
    <div className="space-y-6" data-testid="voice-notes-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A2342] flex items-center gap-2">
            <Mic className="w-6 h-6 text-[#0E7490]" /> Voice Notes
          </h1>
          <p className="text-sm text-[#64748B]">Recorded conversations transcribed by Whisper and summarized by GPT-5.2.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="voice-notes-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
          <Button className="careiq-btn-primary" onClick={() => setOpenNew(true)} data-testid="voice-notes-new-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New voice note
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_1.5fr] gap-4">
        <Card className="careiq-card">
          <CardHeader className="pb-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search notes…" className="pl-8 h-9 text-sm" data-testid="voice-notes-search" />
            </div>
          </CardHeader>
          <CardContent className="p-0 max-h-[70vh] overflow-y-auto">
            {loading && notes.length === 0 && (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-[#0E7490] animate-spin" /></div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="text-center py-12 text-sm text-[#94A3B8]" data-testid="voice-notes-empty">
                <Mic className="w-8 h-8 mx-auto mb-2 text-[#CBD5E1]" />
                {notes.length === 0 ? 'No voice notes yet. Click "New voice note" to record your first.' : 'No notes match your search.'}
              </div>
            )}
            <ul className="divide-y divide-[#F1F5F9]">
              {filtered.map(n => (
                <li
                  key={n.id}
                  onClick={() => setSelected(n)}
                  className={`px-4 py-3 cursor-pointer hover:bg-[#F8FAFC] ${selected?.id === n.id ? 'bg-[#0E7490]/5 border-l-2 border-[#0E7490]' : ''}`}
                  data-testid={`voice-note-item-${n.id}`}
                >
                  <div className="text-sm font-medium text-[#0A2342] truncate">{n.title || 'Untitled'}</div>
                  <div className="text-xs text-[#64748B] truncate mt-0.5">{n.summary || n.transcript?.slice(0, 80) || '(no summary)'}</div>
                  <div className="flex items-center gap-2 mt-1 text-[10px] text-[#94A3B8]">
                    <span>{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</span>
                    {n.patient_name && <span className="px-1.5 py-0.5 rounded bg-[#0E7490]/10 text-[#0E7490]"><UserIcon className="w-2.5 h-2.5 inline mr-0.5" />{n.patient_name}</span>}
                    {n.duration_seconds ? <span>· {Math.round(n.duration_seconds)}s</span> : null}
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card className="careiq-card">
          {selected ? (
            <>
              <CardHeader className="flex flex-row items-start justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="text-lg font-semibold text-[#0A2342]">{selected.title || 'Untitled'}</CardTitle>
                  <div className="text-xs text-[#64748B] mt-0.5">
                    Recorded {selected.created_at ? new Date(selected.created_at).toLocaleString() : ''}
                    {selected.duration_seconds ? ` · ${Math.round(selected.duration_seconds)}s` : ''}
                    {selected.user_name ? ` · ${selected.user_name}` : ''}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleDownload(selected)} data-testid="voice-note-detail-download">
                    <Download className="w-3.5 h-3.5 mr-1.5" /> .txt
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(selected.id)} className="text-[#991B1B] hover:bg-[#991B1B]/5" data-testid="voice-note-detail-delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-xs font-semibold text-[#0A2342] mb-1">Attach to patient</div>
                  <Select value={selected.patient_id || ''} onValueChange={(v) => handleAttachPatient(selected, v === '__none__' ? '' : v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid="voice-note-detail-patient-select"><SelectValue placeholder="Not attached" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__" className="text-xs italic text-[#94A3B8]">Not attached</SelectItem>
                      {patients.map(p => (
                        <SelectItem key={p.id} value={p.id} className="text-xs">
                          {p.first_name} {p.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-xs font-semibold text-[#0A2342] mb-1">Summary</div>
                  <p className="text-sm text-[#0A2342] bg-[#F0FDFA] border border-[#A7F3D0] rounded p-3 whitespace-pre-wrap" data-testid="voice-note-detail-summary">{selected.summary || '(empty)'}</p>
                </div>
                {selected.bullets?.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-[#0A2342] mb-1">Key points</div>
                    <ul className="text-sm text-[#0A2342] list-disc list-inside space-y-0.5">
                      {selected.bullets.map((b, i) => <li key={i}>{b}</li>)}
                    </ul>
                  </div>
                )}
                {selected.action_items?.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-[#0A2342] mb-1">Action items</div>
                    <ol className="text-sm text-[#0A2342] list-decimal list-inside space-y-0.5">
                      {selected.action_items.map((a, i) => <li key={i}>{a}</li>)}
                    </ol>
                  </div>
                )}
                <details>
                  <summary className="cursor-pointer text-[#0E7490] text-xs font-medium">View full transcript</summary>
                  <pre className="mt-2 text-xs text-[#475569] whitespace-pre-wrap bg-[#F8FAFC] p-3 rounded border border-[#E2E8F0] max-h-64 overflow-y-auto">{selected.transcript || '(empty)'}</pre>
                </details>
              </CardContent>
            </>
          ) : (
            <CardContent className="flex flex-col items-center justify-center py-20 text-center">
              <FileText className="w-10 h-10 text-[#CBD5E1] mb-3" />
              <p className="text-sm text-[#64748B]">Select a note from the list to view its summary and transcript.</p>
            </CardContent>
          )}
        </Card>
      </div>

      <VoiceNoteDialog
        open={openNew}
        onOpenChange={setOpenNew}
        onSaved={() => load()}
      />
    </div>
  );
}
